import React, { useEffect, useState } from 'react';
import { DNABox, DNASelect, DNAText, TextField } from '@alucio/lux-ui';
import { IndexPath } from '@ui-kitten/components';

import PublisherSearchResults from 'src/screens/PublisherSearchResults/PublisherSearchResults';
import {
  getMinimumRelativeSearchScore,
  setMinimumRelativeSearchScore,
} from 'src/utils/filterSearchResults';

export const STORAGE_KEY = 'overrideLexicalAdapterName';

const SEARCH_OPTIONS = {
  CURRENT: 'Current Search',
  HYBRID_V2_0: 'Hybrid Search v2.0',
  HYBRID_V2_1: 'Hybrid Search v2.1',
  HYBRID_V2_2: 'Hybrid Search v2.2',
} as const;

export const SearchAdapterSelector = ({ onChange }: { onChange?: (value: string) => void }) => {
  const [selectedValue, setSelectedValue] = useState<string>(SEARCH_OPTIONS.CURRENT);

  useEffect(() => {
    // Initialize from localStorage
    const storedValue = localStorage.getItem(STORAGE_KEY);
    if (storedValue) {
      if (storedValue === 'Hybrid_Search_v2_0') {
        setSelectedValue(SEARCH_OPTIONS.HYBRID_V2_0);
      } else if (storedValue === 'Hybrid_Search_v2_1') {
        setSelectedValue(SEARCH_OPTIONS.HYBRID_V2_1);
      } else if (storedValue === 'Hybrid_Search_v2_2') {
        setSelectedValue(SEARCH_OPTIONS.HYBRID_V2_2);
      }
    }

    // Cleanup function to remove the storage key when component unmounts
    return () => {
      localStorage.removeItem(STORAGE_KEY);
    };
  }, []);

  const handleSelect = async (index: IndexPath | IndexPath[]) => {
    if (Array.isArray(index)) return;

    const selectedOption = Object.values(SEARCH_OPTIONS)[index.row];
    setSelectedValue(selectedOption);

    // Update localStorage based on selection
    switch (selectedOption) {
      case SEARCH_OPTIONS.CURRENT:
        localStorage.removeItem(STORAGE_KEY);
        break;
      case SEARCH_OPTIONS.HYBRID_V2_0:
        localStorage.setItem(STORAGE_KEY, 'Hybrid_Search_v2_0');
        break;
      case SEARCH_OPTIONS.HYBRID_V2_1:
        localStorage.setItem(STORAGE_KEY, 'Hybrid_Search_v2_1');
        break;
      case SEARCH_OPTIONS.HYBRID_V2_2:
        localStorage.setItem(STORAGE_KEY, 'Hybrid_Search_v2_2');
        break;
    }

    onChange?.(selectedOption);
  };

  return (
    <DNASelect
      style={{ width: 200 }}
      value={selectedValue}
      onSelect={handleSelect}
      testID="search-adapter-selector"
    >
      {Object.values(SEARCH_OPTIONS).map((option) => (
        <DNASelect.Item
          key={option}
          title={option}
          testID={`search-adapter-${option.toLowerCase().replace(/\s+/g, '-')}`}
        />
      ))}
    </DNASelect>
  );
};

export const SearchScoreThresholdInput = ({ onChange }: { onChange?: (value: string) => void }) => {
  const [value, setValue] = useState(() => String(getMinimumRelativeSearchScore()));

  const handleChange = (newValue: string) => {
    setValue(newValue);

    const score = Number(newValue);
    if (newValue !== '' && Number.isFinite(score) && score >= 0 && score <= 1) {
      setMinimumRelativeSearchScore(score);
      onChange?.(newValue);
    }
  };

  return (
    <DNABox appearance="col" spacing="xs">
      <DNAText h5>Result score filter</DNAText>
      <TextField.Kitten
        keyboardType="decimal-pad"
        label="Minimum relative score"
        onChangeText={handleChange}
        style={{ width: 200 }}
        testID="minimum-relative-search-score"
        value={value}
      />
      <DNAText>Results below this fraction of the top score are hidden.</DNAText>
    </DNABox>
  );
};

const Search = () => {
  return (
    <DNABox>
      <PublisherSearchResults initUserDefaultFilters={false}/>
    </DNABox>
  );
}

export default Search;
